import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import CategoryMenuBar from "../components/CategoryMenuBar";
import PolicyTextWithFeeHighlight from "../components/PolicyTextWithFeeHighlight";
import { useCart } from "../hooks/useCart";
import { normalizeStorefrontPolicyText } from "../utils/transactionPolicyText";
import { getDirectShopCataloguePath } from "../utils/shopUrls";
import { sortVariantsAscending } from "../utils/productVariantOrdering";
import "./ProductDetail.css";

const ProductDetail = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const shopId = searchParams.get("shopId");
  const bookingId = searchParams.get("bookingId"); // For booking confirmation context

  const [product, setProduct] = useState(null);
  const [shop, setShop] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [globalProducts, setGlobalProducts] = useState([]); // For "You might also like"
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const youMightLikeRef = useRef(null);
  const imageTouchStartXRef = useRef(null);

  const getMasterProductId = (item) => {
    const value = item?.masterProductId;
    if (!value) return "";
    if (typeof value === "object") {
      return String(value._id || value.id || "").trim();
    }
    return String(value).trim();
  };

  const getSellerKey = (item) => {
    const shop =
      item?.shopId && typeof item.shopId === "object" ? item.shopId : null;

    return String(
      item?.displayBusinessSlug ||
        item?.businessSlug ||
        shop?.displayBusinessSlug ||
        shop?.businessId?.slug ||
        shop?.businessId?._id ||
        shop?._id ||
        item?.shopId ||
        "",
    ).trim();
  };

  useEffect(() => {
    fetchProductDetail();
    fetchCategories();
  }, [productId]);

  const fetchProductDetail = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/shop-product/${productId}`,
      );

      if (!response.ok) {
        throw new Error("Product not found");
      }

      const data = await response.json();
      setProduct(data.product);
      setSelectedVariantId(data?.product?._id || null);
      fetchGlobalProducts(data.product);

      // Extract shop info from product.shopId
      if (data.product && data.product.shopId) {
        setShop(data.product.shopId);

        // Fetch related products from the same shop
        try {
          const relatedResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/shop/${data.product.shopId._id}?limit=6&exclude=${productId}`,
          );
          if (relatedResponse.ok) {
            const relatedData = await relatedResponse.json();
            const relatedWithoutCurrent = (relatedData.products || []).filter(
              (p) => p?._id && p._id !== productId,
            );
            setRelatedProducts(relatedWithoutCurrent.slice(0, 5)); // Show max 5 related products
          }
        } catch (error) {
          setRelatedProducts([]);
        }
      } else {
        setRelatedProducts([]);
      }
    } catch (error) {
      setError("Failed to load product details");
      setGlobalProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/product-categories`,
      );
      if (response.ok) {
        const data = await response.json();
        setCategories(data || []); // API returns array directly
      } else {
        setCategories([]);
      }
    } catch (error) {
      setCategories([]);
    }
  };

  const fetchGlobalProducts = async (currentProduct) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/v2/products/featured?limit=6`,
      );
      if (response.ok) {
        const data = await response.json();
        const currentShopProductId = String(currentProduct?._id || productId).trim();
        const currentMasterProductId = getMasterProductId(currentProduct);
        const currentSellerKey = getSellerKey(currentProduct);

        const filtered =
          data.products?.filter((candidate) => {
            const candidateShopProductId = String(candidate?._id || "").trim();

            if (
              currentShopProductId &&
              candidateShopProductId === currentShopProductId
            ) {
              return false;
            }

            const candidateMasterProductId = getMasterProductId(candidate);
            const candidateSellerKey = getSellerKey(candidate);

            const sameMasterProduct =
              currentMasterProductId &&
              candidateMasterProductId &&
              candidateMasterProductId === currentMasterProductId;

            const sameSeller =
              currentSellerKey &&
              candidateSellerKey &&
              candidateSellerKey === currentSellerKey;

            // Hide the currently viewed product for the same seller,
            // but allow it when offered by a different seller.
            if (sameMasterProduct && sameSeller) {
              return false;
            }

            return true;
          }) || [];

        setGlobalProducts(filtered.slice(0, 5));
      }
    } catch (error) {
      setGlobalProducts([]);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;

    const activeVariant =
      Array.isArray(product.variants) && selectedVariantId
        ? product.variants.find((variant) => String(variant._id) === String(selectedVariantId))
        : null;

    const selectedProductId = activeVariant?._id || product._id;
    const selectedVariantStock =
      activeVariant && typeof activeVariant.stock === "number"
        ? activeVariant.stock
        : typeof product.stock === "number"
          ? product.stock
          : null;

    if (selectedVariantStock !== null && selectedVariantStock <= 0) {
      return;
    }

    try {
      setAddingToCart(true);

      await addToCart({
        productId: selectedProductId,
        quantity: quantity,
        shopId:
          shopId ||
          (typeof product.shopId === "object"
            ? product.shopId._id
            : product.shopId),
        pickupAppointmentId: bookingId || undefined, // Link to booking if from confirmation page
      });

      // Show success feedback
      // TODO: Add toast notification or success message
    } catch (error) {
      // TODO: Show error message
    } finally {
      setAddingToCart(false);
    }
  };

  const handleRelatedProductClick = (relatedProduct) => {
    const relatedShopId =
      typeof relatedProduct.shopId === "object"
        ? relatedProduct.shopId._id
        : relatedProduct.shopId;
    navigate(`/product/${relatedProduct._id}?shopId=${relatedShopId}`);
  };

  const handleCategoryClick = (categoryValue) => {
    setSelectedCategory(categoryValue);
    if (categoryValue === "all") {
      navigate("/products");
    } else {
      navigate(`/products?category=${categoryValue}`);
    }
  };

  const navigateToShopProducts = () => {
    const shopIdentifier = String(
      shop?._id ||
        (typeof product?.shopId === "object"
          ? product?.shopId?._id
          : product?.shopId) ||
        "",
    ).trim();

    const directShopPath =
      getDirectShopCataloguePath(product?.professionalId) ||
      getDirectShopCataloguePath(product?.shopId) ||
      getDirectShopCataloguePath(shop);

    if (directShopPath) {
      navigate(directShopPath);
      return;
    }

    if (shop?.isVirtual && shop?.staffId) {
      navigate(`/freelancer/${shop.staffId}`);
      return;
    }

    if (shop?.businessId?.slug && shop?.slug) {
      navigate(`/business/${shop.businessId.slug}/${shop.slug}/catalogue`);
      return;
    }

    if (shopIdentifier) {
      navigate(`/shop/${encodeURIComponent(shopIdentifier)}/products`);
      return;
    }

    navigate("/products");
  };

  const scrollYouMightLike = (direction) => {
    if (!youMightLikeRef.current) return;
    const offset = direction === "left" ? -220 : 220;
    youMightLikeRef.current.scrollBy({ left: offset, behavior: "smooth" });
  };

  const handleCarouselArrowClick = (event, direction) => {
    event.preventDefault();
    event.currentTarget.blur();
    scrollYouMightLike(direction);
  };

  const handleMainImageNavigate = (direction) => {
    if (displayImages.length <= 1) return;

    setSelectedImageIndex((prev) => {
      if (direction === "prev") {
        return prev === 0 ? displayImages.length - 1 : prev - 1;
      }

      return prev === displayImages.length - 1 ? 0 : prev + 1;
    });
  };

  const handleMainImageTouchStart = (event) => {
    if (displayImages.length <= 1) return;
    imageTouchStartXRef.current = event.touches?.[0]?.clientX ?? null;
  };

  const handleMainImageTouchEnd = (event) => {
    if (displayImages.length <= 1) return;

    const startX = imageTouchStartXRef.current;
    const endX = event.changedTouches?.[0]?.clientX ?? null;
    imageTouchStartXRef.current = null;

    if (startX == null || endX == null) return;

    const deltaX = endX - startX;
    if (Math.abs(deltaX) < 32) return;

    handleMainImageNavigate(deltaX < 0 ? "next" : "prev");
  };

  if (loading) {
    return (
      <>
        <CustomerHeader />
        <div className="product-detail-loading">
          <div className="product-detail-loading-text">Loading product details...</div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (error || !product) {
    return (
      <>
        <CustomerHeader />
        <div className="product-detail-error">
          <h2>Product Not Found</h2>
          <p>
            {error || "The product you are looking for could not be found."}
          </p>
          <button onClick={() => navigate(-1)} className="btn-primary">
            Go Back
          </button>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const variants = Array.isArray(product.variants) ? product.variants : [];
  const sortedVariants = sortVariantsAscending(variants);
  const selectedVariant =
    sortedVariants.find((variant) => String(variant._id) === String(selectedVariantId)) || null;
  const variantImages = Array.isArray(selectedVariant?.variantImages)
    ? selectedVariant.variantImages.filter(Boolean)
    : [];
  const baseImages = Array.isArray(product.images)
    ? product.images.filter(Boolean)
    : [product.imageUrl].filter(Boolean);
  const displayImages = (variantImages.length > 0 ? variantImages : baseImages).slice(0, 4);
  const currentImage = displayImages[selectedImageIndex] || "/default-product.png";
  const effectivePrice =
    typeof selectedVariant?.price === "number"
      ? selectedVariant.price
      : product.price;
  const effectiveStock =
    typeof selectedVariant?.stock === "number"
      ? selectedVariant.stock
      : typeof product.stock === "number"
        ? product.stock
        : null;
  const isOutOfStock = effectiveStock !== null ? effectiveStock <= 0 : product.inStock === false;
  const fullDescription = String(product?.description || "").trim();
  const descriptionWords = fullDescription ? fullDescription.split(/\s+/).filter(Boolean) : [];
  const descriptionExcerptLimit = 80;
  const descriptionExcerpt = descriptionWords
    .slice(0, descriptionExcerptLimit)
    .join(" ");
  const hasDescriptionOverflow = descriptionWords.length > descriptionExcerptLimit;
  const storefrontPolicyTerms = String(
    product?.shopId?.displayPolicyTerms || "",
  ).trim();
  const storefrontPolicyRefund = normalizeStorefrontPolicyText(
    String(product?.shopId?.displayPolicyRefund || "").trim(),
  );
  const storefrontPolicyCancellation = normalizeStorefrontPolicyText(
    String(product?.shopId?.displayPolicyCancellation || "").trim(),
  );

  return (
    <>
      <CustomerHeader />

      {/* Category Menu Bar - Mobile Only (fixed at top) */}
      <CategoryMenuBar
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryClick={handleCategoryClick}
        mode="mobile-only"
        showCategoriesLabel={false}
      />

      <div className="product-detail-container">
        <div className="product-page-layout">
          {/* Category Sidebar - Desktop Only (Column 1) */}
          <CategoryMenuBar
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryClick={handleCategoryClick}
            mode="sidebar-only"
            showCategoriesLabel={false}
          />

          {/* Main Content Area (Column 2) */}
          <main className="product-main-content">
            {/* Shop/Business Information - Prominent Section */}
            {shop && (
              <div className="shop-header-section">
                <div className="shop-info-card">
                  {(shop.logo || shop.profilePicture) && (
                    <div className="business-logo">
                      <img
                        src={shop.logo || shop.profilePicture}
                        alt={`${shop.displayBrandName || shop.displayBusinessName || shop.businessId?.name || "Shop"} logo`}
                        className="logo-image"
                      />
                    </div>
                  )}
                  <div className="shop-details">
                    <h1 className="business-name">
                      {shop.displayBrandName ||
                        shop.displayBusinessName ||
                        shop.businessId?.name ||
                        "Shop"}
                    </h1>
                    {(shop.displayCity || shop.city) &&
                      (shop.displayState || shop.state) && (
                        <p className="business-location">
                          {shop.displayCity || shop.city},{" "}
                          {shop.displayState || shop.state}
                        </p>
                      )}
                  </div>
                  <div className="shop-actions">
                    <button
                      onClick={navigateToShopProducts}
                      className="visit-shop-btn"
                    >
                      {shop.isVirtual
                        ? "View Freelancer"
                        : "Visit Shop Products"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="product-detail-content">
              {/* Product Images */}
              <div className="product-images-section">
                {/* Main Image */}
                <div
                  className={`main-image-container ${displayImages.length > 1 ? "main-image-container--interactive" : ""}`}
                  onClick={() => handleMainImageNavigate("next")}
                  onTouchStart={handleMainImageTouchStart}
                  onTouchEnd={handleMainImageTouchEnd}
                >
                  <img
                    src={currentImage}
                    alt={product.name}
                    className="main-product-image"
                  />
                  {isOutOfStock && (
                    <div className="out-of-stock-overlay">
                      <span>Out of Stock</span>
                    </div>
                  )}

                  {displayImages.length > 1 && (
                    <div className="main-image-dots" aria-label="Product image selectors">
                      {displayImages.map((image, index) => (
                        <button
                          key={`${index}-${image}`}
                          type="button"
                          className={`main-image-dot ${index === selectedImageIndex ? "active" : ""}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedImageIndex(index);
                          }}
                          aria-label={`View image ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Product Information */}
              <div className="product-info-section">
                {/* Header */}
                <div className="product-header">
                  {product.brand && (
                    <span className="product-brand">{product.brand}</span>
                  )}
                  <h1 className="product-title">{product.name}</h1>
                  <div className="product-price">
                    $
                    {typeof effectivePrice === "number"
                      ? effectivePrice.toFixed(2)
                      : effectivePrice}
                  </div>
                </div>

                {variants.length > 1 && (
                  <div style={{ marginBottom: "16px" }}>
                    <h3 style={{ margin: "0 0 10px 0", fontSize: "14px", fontWeight: "600" }}>
                      Select Option
                    </h3>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {sortedVariants.map((variant) => {
                        const isSelected = String(variant._id) === String(selectedVariantId);
                        const variantOutOfStock =
                          variant.variantIsActive === false ||
                          (parseInt(variant.stock, 10) || 0) <= 0;

                        return (
                          <button
                            key={variant._id}
                            type="button"
                            onClick={() => {
                              setSelectedVariantId(variant._id);
                              setSelectedImageIndex(0);
                            }}
                            disabled={variantOutOfStock}
                            style={{
                              padding: "8px 12px",
                              borderRadius: "8px",
                              border: isSelected ? "2px solid #111827" : "1px solid #d1d5db",
                              background: variantOutOfStock ? "#f3f4f6" : "#fff",
                              color: variantOutOfStock ? "#9ca3af" : "#111827",
                              cursor: variantOutOfStock ? "not-allowed" : "pointer",
                              textDecoration: variantOutOfStock ? "line-through" : "none",
                              fontWeight: isSelected ? "600" : "500",
                              fontSize: "13px",
                            }}
                            title={variantOutOfStock ? "Out of stock" : "In stock"}
                          >
                            {variant.variantLabel || variant.variantKey || "Default"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Category Badge */}
                {product.category && (
                  <div className="product-category">
                    <span className="category-badge">{product.category}</span>
                  </div>
                )}

                {/* Description */}
                {fullDescription && (
                  <div className="product-description">
                    <h3>About This Item</h3>
                    <p>
                      {descriptionExcerpt}
                      {hasDescriptionOverflow ? "..." : ""}
                    </p>
                  </div>
                )}

                {/* Key Ingredients */}
                {product.ingredients && (
                  <div className="product-ingredients">
                    <h3>Key Ingredients</h3>
                    <p>{product.ingredients}</p>
                  </div>
                )}

                {/* Benefits */}
                {product.benefits && (
                  <div className="product-benefits">
                    <h3>Benefits</h3>
                    <p>{product.benefits}</p>
                  </div>
                )}

                {/* How to Use */}
                {product.howToUse && (
                  <div className="product-usage">
                    <h3>How to Use</h3>
                    <p>{product.howToUse}</p>
                  </div>
                )}

                {/* Add to Cart Section */}
                <div className="add-to-cart-section">
                  <div className="quantity-selector">
                    <label>Quantity:</label>
                    <div className="quantity-controls">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                        className="quantity-btn"
                      >
                        -
                      </button>
                      <span className="quantity-display">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="quantity-btn"
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={handleAddToCart}
                    disabled={isOutOfStock || addingToCart}
                    className="add-to-cart-btn"
                  >
                    {addingToCart ? "Adding..." : isOutOfStock ? "Out of Stock" : "Add to Cart"}
                  </button>
                </div>

              </div>
            </div>

            {fullDescription && (
              <div className="product-full-description-section">
                <h2>Product Information</h2>
                <p>{fullDescription}</p>
              </div>
            )}

            {/* Related Products Section */}
            {/* Related Products - From same shop */}
            {relatedProducts.length > 0 && (
              <div className="related-products-section">
                <div className="section-header-with-button">
                  <h3>Related Products</h3>
                  {shop && (
                    <button
                      className="show-all-button"
                      onClick={navigateToShopProducts}
                    >
                      Show All
                    </button>
                  )}
                </div>
                <div className="related-products-grid">
                  {relatedProducts.slice(0, 5).map((relatedProduct) => (
                    <div
                      key={relatedProduct._id}
                      className="related-product-card"
                      onClick={() => handleRelatedProductClick(relatedProduct)}
                    >
                      <div className="related-product-image">
                        <img
                          src={
                            relatedProduct.images?.[0] ||
                            relatedProduct.imageUrl ||
                            "/default-product.png"
                          }
                          alt={relatedProduct.name}
                        />
                      </div>
                      <div className="related-product-info">
                        <h4>{relatedProduct.name}</h4>
                        <p className="related-product-price">
                          ${relatedProduct.price?.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* You Might Also Like - Global products */}
            {globalProducts.length > 0 && (
              <div className="you-might-like-section">
                <div className="section-header-with-button">
                  <h3>You might also like</h3>
                  <button
                    className="show-all-button"
                    onClick={() => navigate("/products")}
                  >
                    Show All
                  </button>
                </div>
                <div className="you-might-like-carousel">
                  <button
                    type="button"
                    className="carousel-arrow carousel-arrow-left"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => handleCarouselArrowClick(e, "left")}
                    aria-label="Scroll left"
                  >
                    &lt;
                  </button>
                  <div className="you-might-like-grid" ref={youMightLikeRef}>
                    {globalProducts.map((globalProduct) => (
                      <div
                        key={globalProduct._id}
                        className="related-product-card"
                        onClick={() => handleRelatedProductClick(globalProduct)}
                      >
                        <div className="related-product-image">
                          <img
                            src={
                              globalProduct.images?.[0] ||
                              globalProduct.imageUrl ||
                              "/default-product.png"
                            }
                            alt={globalProduct.name}
                          />
                        </div>
                        <div className="related-product-info">
                          <h4>{globalProduct.name}</h4>
                          <p className="related-product-price">
                            ${globalProduct.price?.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="carousel-arrow carousel-arrow-right"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => handleCarouselArrowClick(e, "right")}
                    aria-label="Scroll right"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}

            {(storefrontPolicyTerms ||
              storefrontPolicyRefund ||
              storefrontPolicyCancellation) && (
              <div className="product-policy-section product-policy-section--below-recommendations">
                <h3>Store Policies</h3>
                {storefrontPolicyCancellation && (
                  <details>
                    <summary>Cancellation Policy</summary>
                    <PolicyTextWithFeeHighlight text={storefrontPolicyCancellation} />
                  </details>
                )}
                {storefrontPolicyRefund && (
                  <details>
                    <summary>Refund Policy</summary>
                    <PolicyTextWithFeeHighlight text={storefrontPolicyRefund} />
                  </details>
                )}
                {storefrontPolicyTerms && (
                  <details>
                    <summary>Terms and Conditions</summary>
                    <PolicyTextWithFeeHighlight text={storefrontPolicyTerms} />
                  </details>
                )}
              </div>
            )}
          </main>
        </div>
      </div>

      <CustomerFooter />
    </>
  );
};

export default ProductDetail;
